/**
 * Loading screen com identidade envpulse — hexágono pulsando + ECG.
 *
 * Usado em rotas e em telas que precisam aguardar dados críticos.
 */

import clsx from "clsx";

interface Props {
  label?: string;
  /** Variante compacta (60px) ou tela cheia (default). */
  size?: "sm" | "md" | "lg";
  /** Substitui o min-h-[60vh] (útil quando renderiza dentro de card). */
  fullscreen?: boolean;
  className?: string;
}

const SIZE_MAP = {
  sm: { svg: 32, label: "text-xs" },
  md: { svg: 56, label: "text-sm" },
  lg: { svg: 80, label: "text-base" },
} as const;

export function Loading({ label = "Carregando…", size = "md", fullscreen = true, className }: Props) {
  const s = SIZE_MAP[size];
  return (
    <div
      className={clsx(
        "flex items-center justify-center",
        fullscreen && "min-h-[60vh]",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-4 text-slate-500 dark:text-slate-400">
        <EnvpulseSpinner size={s.svg} />
        {label && <span className={clsx(s.label, "animate-pulse")}>{label}</span>}
      </div>
    </div>
  );
}

/**
 * Hexágono ciano + ECG branco com 2 animações sutis:
 *   1. O hex pulsa (scale + opacity)
 *   2. A linha do ECG "desenha" sozinha (stroke-dashoffset)
 */
export function EnvpulseSpinner({ size = 56 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      xmlns="http://www.w3.org/2000/svg"
      role="status"
      aria-label="Carregando"
      className="envpulse-spinner"
    >
      <style>{`
        .envpulse-spinner .hex { animation: envpulse-hex 2s ease-in-out infinite; transform-origin: 100px 100px; }
        .envpulse-spinner .ecg { stroke-dasharray: 250; animation: envpulse-ecg 1.6s ease-in-out infinite; }
        .envpulse-spinner .dot { animation: envpulse-dot 1.6s ease-in-out infinite; transform-origin: 88px 55px; }
        @keyframes envpulse-hex {
          0%, 100% { transform: scale(1);    opacity: 0.85; }
          50%      { transform: scale(1.05); opacity: 1; }
        }
        @keyframes envpulse-ecg {
          0%   { stroke-dashoffset: 250; }
          60%  { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: -250; }
        }
        @keyframes envpulse-dot {
          0%, 40%  { opacity: 0;   transform: scale(0.5); }
          60%      { opacity: 1;   transform: scale(1.4); }
          100%     { opacity: 0;   transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .envpulse-spinner .hex,
          .envpulse-spinner .ecg,
          .envpulse-spinner .dot { animation: none; }
        }
      `}</style>
      <polygon
        className="hex"
        points="180,100 140,30.7 60,30.7 20,100 60,169.3 140,169.3"
        fill="none"
        stroke="#22D3EE"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      <polyline
        className="ecg"
        points="35,100 70,100 78,108 88,55 96,140 104,80 112,100 165,100"
        fill="none"
        stroke="#E8EEF7"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle className="dot" cx="88" cy="55" r="4" fill="#FFFFFF" />
    </svg>
  );
}
